import React, { useEffect, useMemo } from 'react'

import { generateHref } from 'Helmet/GoustoHelmet'
import Helmet from 'react-helmet'

import { RibbonTriggerContainer } from 'components/RibbonTrigger'
import routesConfig from 'config/routes'

import { Carousel } from './Carousel'
import { Hero } from './Hero'
import { HighlightChoice } from './HighlightChoice'
import { HowGoustoWorks } from './HowGoustoWorks'
import { JoeWicks } from './JoeWicks'
import { PriceComparisonTable } from './PriceComparisonTable'
import { PromoBanner } from './PromoBanner'
import { TrustPilot } from './TrustPilot'
import { ValueForMoney } from './ValueForMoney/ValueForMoney'
import { WhyChooseGousto } from './WhyChooseGousto'
import { knownVariants, defaultVariant, seo } from './homeConfig'
import { useGetHomePageExperiments, useGetMenuData } from './homeHooks'

const getHelmetLink = (variant: string) => {
  const {
    client: { home },
  } = routesConfig
  const knownVariant = knownVariants.includes(variant) ? variant : defaultVariant
  const link = knownVariant !== 'default' ? [{ rel: 'canonical', href: generateHref(home) }] : []

  return link
}

type HomePageProps = { location: { query: { variant: string } } }

export const HomePageContent = ({ location }: HomePageProps) => {
  const { isPriceComparisonTableEnabled, isHowGoustoWorksEnabled, isValueForMoneyEnabled } =
    useGetHomePageExperiments()
  const {
    dispatchRedirectLoggedInUser,
    dispatchMenuFetchData,
    dispatchMenuLoadBoxPrices,
    pricePerServing,
  } = useGetMenuData()

  dispatchRedirectLoggedInUser()

  useEffect(() => {
    if (!pricePerServing) dispatchMenuLoadBoxPrices()

    const prefetchTimer = setTimeout(() => {
      dispatchMenuFetchData()
      clearTimeout(prefetchTimer)
    }, 500)

    return () => {
      clearTimeout(prefetchTimer)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const link = useMemo(() => getHelmetLink(location.query.variant), [location.query.variant])

  return (
    <>
      <Helmet
        title={seo.title}
        meta={seo.meta}
        link={link}
        style={[
          {
            cssText: `
              #react-root {
                height: 100%;
              }
            `,
          },
        ]}
      />

      <>
        <PromoBanner />
        <section data-module-name="">
          <Hero />
        </section>
        <section data-module-name="">
          <HighlightChoice />
        </section>
        {isPriceComparisonTableEnabled && (
          <section data-module-name="">
            <PriceComparisonTable />
          </section>
        )}
        {isHowGoustoWorksEnabled && (
          <section data-module-name="">
            <HowGoustoWorks />
          </section>
        )}
        <section data-module-name="">
          <TrustPilot />
        </section>
        {isValueForMoneyEnabled && (
          <section data-module-name="">
            <ValueForMoney />
          </section>
        )}
        <section data-module-name="">
          <WhyChooseGousto />
        </section>
        <section data-module-name="">
          <JoeWicks />
        </section>
        <section data-module-name="">
          <Carousel />
        </section>
      </>

      <RibbonTriggerContainer name="home-page" />
    </>
  )
}
